import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import whatsappNotificationService from '../../services/whatsappNotificationService';
import AdminNavigation from '../../components/ui/AdminNavigation';
import Button from '../../components/ui/Button';

export default function WhatsAppNotificationsManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [webhookConfigs, setWebhookConfigs] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('webhooks');
  const [editingConfig, setEditingConfig] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [configs, templates] = await Promise.all([
        whatsappNotificationService?.getWebhookConfigs(),
        whatsappNotificationService?.getMessageTemplates()
      ]);

      setWebhookConfigs(configs || []);
      setMessageTemplates(templates || []);
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebhookConfig = async (provider, config) => {
    try {
      setError('');
      setSuccess('');
      
      await whatsappNotificationService?.updateWebhookConfig(provider, config);
      
      setSuccess(`Configuration ${provider} mise à jour avec succès`);
      setEditingConfig(null);
      loadData();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleSaveTemplate = async (status, messageFr, messageEn) => {
    try {
      setError('');
      setSuccess('');
      
      await whatsappNotificationService?.updateMessageTemplate(status, messageFr, messageEn);
      
      setSuccess(`Modèle ${status} mis à jour avec succès`);
      setEditingTemplate(null);
      loadData();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Notifications WhatsApp</h1>
              <p className="mt-2 text-gray-600">Configuration des webhooks et modèles de messages</p>
            </div>
            <Button
              onClick={() => navigate('/admin/notifications')}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Voir les Logs
            </Button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('webhooks')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'webhooks' ?'border-b-2 border-green-600 text-green-600' :'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Configuration Webhooks
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'templates' ?'border-b-2 border-green-600 text-green-600' :'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Modèles de Messages
              </button>
            </nav>
          </div>

          {/* Webhooks Tab */}
          {activeTab === 'webhooks' && (
            <div className="p-6">
              <div className="space-y-6">
                {webhookConfigs?.map(config => (
                  <WebhookConfigCard
                    key={config?.provider}
                    config={config}
                    isEditing={editingConfig === config?.provider}
                    onEdit={() => setEditingConfig(config?.provider)}
                    onCancel={() => setEditingConfig(null)}
                    onSave={handleSaveWebhookConfig}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="p-6">
              <div className="space-y-6">
                {messageTemplates?.map(template => (
                  <MessageTemplateCard
                    key={template?.shipment_status}
                    template={template}
                    isEditing={editingTemplate === template?.shipment_status}
                    onEdit={() => setEditingTemplate(template?.shipment_status)}
                    onCancel={() => setEditingTemplate(null)}
                    onSave={handleSaveTemplate}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WebhookConfigCard({ config, isEditing, onEdit, onCancel, onSave }) {
  const [formData, setFormData] = useState({
    webhookUrl: config?.webhook_url,
    apiKey: config?.api_key || '',
    isActive: config?.is_active,
    configData: config?.config_data
  });

  const providerLabels = {
    n8n: 'n8n',
    make: 'Make.com',
    manychat: 'ManyChat',
    whatsapp_cloud: 'WhatsApp Cloud API'
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(config?.provider, formData);
  };

  if (!isEditing) {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              {providerLabels?.[config?.provider]}
            </h3>
            <span className={`ml-3 px-3 py-1 rounded-full text-sm ${
              config?.is_active 
                ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
            }`}>
              {config?.is_active ? 'Actif' : 'Inactif'}
            </span>
          </div>
          <Button
            onClick={onEdit}
            className="bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Modifier
          </Button>
        </div>
        <div className="space-y-2 text-sm">
          <p className="text-gray-600">
            <span className="font-medium">URL:</span> {config?.webhook_url}
          </p>
          {config?.api_key && (
            <p className="text-gray-600">
              <span className="font-medium">API Key:</span> ••••••••
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {providerLabels?.[config?.provider]}
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            URL du Webhook
          </label>
          <input
            type="url"
            value={formData?.webhookUrl}
            onChange={(e) => setFormData({ ...formData, webhookUrl: e?.target?.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            API Key (optionnel)
          </label>
          <input
            type="password"
            value={formData?.apiKey}
            onChange={(e) => setFormData({ ...formData, apiKey: e?.target?.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id={`active-${config?.provider}`}
            checked={formData?.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e?.target?.checked })}
            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
          />
          <label htmlFor={`active-${config?.provider}`} className="ml-2 text-sm text-gray-700">
            Activer ce provider
          </label>
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <Button type="submit" className="bg-green-600 text-white hover:bg-green-700">
          Enregistrer
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          className="bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          Annuler
        </Button>
      </div>
    </form>
  );
}

function MessageTemplateCard({ template, isEditing, onEdit, onCancel, onSave }) {
  const [formData, setFormData] = useState({
    messageFr: template?.message_fr,
    messageEn: template?.message_en || ''
  });

  const statusLabels = {
    pending: 'En attente',
    in_transit: 'En transit',
    customs: 'Dédouanement',
    out_for_delivery: 'En livraison',
    delivered: 'Livré',
    exception: 'Exception'
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(template?.shipment_status, formData?.messageFr, formData?.messageEn);
  };

  if (!isEditing) {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {statusLabels?.[template?.shipment_status]}
          </h3>
          <Button
            onClick={onEdit}
            className="bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Modifier
          </Button>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-700">Message (FR):</p>
            <p className="text-sm text-gray-600 mt-1">{template?.message_fr}</p>
          </div>
          {template?.message_en && (
            <div>
              <p className="text-sm font-medium text-gray-700">Message (EN):</p>
              <p className="text-sm text-gray-600 mt-1">{template?.message_en}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {statusLabels?.[template?.shipment_status]}
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message (Français)
          </label>
          <textarea
            value={formData?.messageFr}
            onChange={(e) => setFormData({ ...formData, messageFr: e?.target?.value })}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Variables disponibles: {'{reference}'}, {'{receiver_name}'}, {'{status}'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message (Anglais - optionnel)
          </label>
          <textarea
            value={formData?.messageEn}
            onChange={(e) => setFormData({ ...formData, messageEn: e?.target?.value })}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <Button type="submit" className="bg-green-600 text-white hover:bg-green-700">
          Enregistrer
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          className="bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          Annuler
        </Button>
      </div>
    </form>
  );
}